import { NavLink } from 'react-router-dom';
import { FileIcon } from '../files/FileIcon.tsx';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../store/auth.ts';
import { useUi } from '../../store/ui.ts';
import { usage } from '../../api/devices.ts';
import { formatBytes } from '../../lib/format.ts';
import {
  IconClock, IconCloud, IconDevice, IconDownload, IconSettings, IconShare, IconTrash,
} from '../../lib/icons.tsx';

const LINKS = [
  { to: '/files', label: '내 드라이브', icon: <IconCloud size={17} />, feature: null },
  { to: '/recent', label: '최근 항목', icon: <IconClock size={17} />, feature: null },
  { to: '/shared', label: '공유', icon: <IconShare size={17} />, feature: 'sharing' as const },
  { to: '/trash', label: '휴지통', icon: <IconTrash size={17} />, feature: null },
];

export function Sidebar() {
  const open = useUi((s) => s.sidebarOpen);
  const user = useAuth((s) => s.user);
  const namespaces = useAuth((s) => s.namespaces);
  const sharing = useAuth((s) => s.features.sharing);
  const shared = namespaces.filter((n) => n.type === 'shared');
  const links = LINKS.filter((l) => l.feature === null || sharing);

  const { data } = useQuery({
    queryKey: ['usage'],
    queryFn: usage,
    staleTime: 60_000,
  });

  const used = data?.used_bytes ?? user?.used_bytes ?? 0;
  const quota = data?.quota_bytes ?? user?.quota_bytes ?? 1;
  const percent = Math.min(100, Math.round((used / Math.max(1, quota)) * 100));
  const level = percent >= 95 ? 'full' : percent >= 80 ? 'warn' : 'ok';

  return (
    <nav className="sidebar" data-open={open} aria-label="주요 메뉴">
      <div className="sidebar__nav">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className="sidebar__link"
            end={link.to === '/files'}
          >
            {link.icon} {link.label}
          </NavLink>
        ))}
      </div>

      {sharing && shared.length > 0 && (
        <>
          <div className="sidebar__section">공유 폴더</div>
          <div className="sidebar__nav">
            {shared.map((ns) => (
              <NavLink key={ns.id} to={`/ns/${ns.id}`} className="sidebar__link">
                <FileIcon node={{ type: 'folder', name: ns.name, mime: null }} size={17} />
                <span className="truncate">{ns.name}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}

      <div className="sidebar__section">기기</div>
      <div className="sidebar__nav">
        <NavLink to="/devices" className="sidebar__link"><IconDevice size={17} /> 연결된 PC</NavLink>
        <NavLink to="/account" className="sidebar__link"><IconSettings size={17} /> 내 계정</NavLink>
        <a href="/download" className="sidebar__link"><IconDownload size={17} /> 앱 다운로드</a>
        {user?.role === 'admin' && (
          <NavLink to="/admin" className="sidebar__link"><IconSettings size={17} /> 관리</NavLink>
        )}
      </div>

      <div className="quota">
        <div className="quota__bar">
          <div className="quota__fill" data-level={level} style={{ width: `${percent}%` }} />
        </div>
        <div className="quota__text">
          {formatBytes(used)} / {formatBytes(quota)} 사용 ({percent}%)
          {data && data.trash_bytes > 0 && (
            <div className="faint" style={{ fontSize: 11 }}>
              휴지통 {formatBytes(data.trash_bytes)}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
